/**
 * Seatics CTA copy helpers for the pre-built UI.
 *
 * Per-row ticket-group CTAs are rendered natively via
 * `Seatics.config.buyButtonContentHtml` (see `seaticsUiConfig.js`). We also
 * relabel any leftover "Buy Tickets" row buttons in the DOM, and pin the
 * quantity-panel `#pre-checkout-price-cta` to "Go to Secure Checkout".
 */

export const PROMOTIX_TICKET_GROUP_CTA_LABEL = 'Select Tickets';
export const PROMOTIX_PRE_CHECKOUT_CTA_LABEL = 'Go to Secure Checkout';

export const PROMOTIX_SEATICS_BUY_CTA_PATCHED_ATTR = 'data-promotix-buy-cta-patched';
export const PROMOTIX_PRE_CHECKOUT_PIN_ATTR = 'data-promotix-precheckout-pinned';

const PRECHECKOUT_CTA_SELECTOR =
  '#pre-checkout-price-cta, button[id*="pre-checkout"][id*="cta"], a[id*="pre-checkout"][id*="cta"]';

/** @type {WeakMap<HTMLElement, MutationObserver>} */
const preCheckoutPinObservers = new WeakMap();

export const TICKET_ROW_PRICE_TEXT_RE =
  /^\$\d+(?:[.,]\d+)?\s*\/\s*ea(?:\s*[\u203a\u00bb\u25b6>])?\s*$/i;

export const normalizeSeaticsText = text =>
  String(text || '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const isTicketRowPriceText = text => {
  const t = normalizeSeaticsText(text);
  return t.length > 0 && t.length <= 36 && TICKET_ROW_PRICE_TEXT_RE.test(t);
};

/** Quantity-panel primary CTA — always "Go to Secure Checkout". */
export const formatPreCheckoutCtaLabel = () => PROMOTIX_PRE_CHECKOUT_CTA_LABEL;

export const enforcePreCheckoutCtaLabel = btn => {
  if (!(btn instanceof HTMLElement)) return false;
  if (normalizeSeaticsText(btn.textContent) === PROMOTIX_PRE_CHECKOUT_CTA_LABEL) {
    btn.setAttribute(PROMOTIX_SEATICS_BUY_CTA_PATCHED_ATTR, '1');
    return false;
  }
  btn.textContent = PROMOTIX_PRE_CHECKOUT_CTA_LABEL;
  btn.setAttribute(PROMOTIX_SEATICS_BUY_CTA_PATCHED_ATTR, '1');
  btn.setAttribute('aria-label', PROMOTIX_PRE_CHECKOUT_CTA_LABEL);
  return true;
};

/**
 * Keep the quantity-panel CTA pinned. TN rewrites "Checkout" on every qty tap;
 * a dedicated observer restores the label synchronously (no debounce).
 */
export const pinPreCheckoutCtaLabel = btn => {
  if (!(btn instanceof HTMLElement) || typeof MutationObserver === 'undefined') {
    return enforcePreCheckoutCtaLabel(btn);
  }

  const changed = enforcePreCheckoutCtaLabel(btn);

  if (preCheckoutPinObservers.has(btn)) {
    return changed;
  }

  const observer = new MutationObserver(() => {
    // Disconnect while writing so our own mutation doesn't re-enter.
    observer.disconnect();
    enforcePreCheckoutCtaLabel(btn);
    observer.observe(btn, { characterData: true, childList: true, subtree: true });
  });
  observer.observe(btn, { characterData: true, childList: true, subtree: true });
  preCheckoutPinObservers.set(btn, observer);
  btn.setAttribute(PROMOTIX_PRE_CHECKOUT_PIN_ATTR, '1');
  return changed;
};

/** Relabel native ticket-group row CTAs to "Select Tickets" (not pre-checkout). */
export const patchTicketGroupRowCtaLabelsInRoot = root => {
  if (!(root instanceof Element)) return false;
  let patched = false;
  root
    .querySelectorAll(
      '.venue-ticket-list-cta-button, .promotix-seatics-buy-cta, button.venue-ticket-list-cta-js'
    )
    .forEach(btn => {
      if (!(btn instanceof HTMLElement)) return;
      if (btn.id === 'pre-checkout-price-cta') return;
      if (btn.closest?.('#pre-checkout-price-cta')) return;
      const current = normalizeSeaticsText(btn.textContent);
      if (!current || !/^buy tickets$/i.test(current)) return;
      btn.textContent = PROMOTIX_TICKET_GROUP_CTA_LABEL;
      patched = true;
    });
  return patched;
};

const shouldRelabelPreCheckoutCta = (btn, current) => {
  if (btn.id === 'pre-checkout-price-cta') return true;
  if (/pre-checkout/i.test(btn.id || '') && /cta/i.test(btn.id || '')) return true;
  return (
    /^checkout$/i.test(current) ||
    /^go to checkout$/i.test(current) ||
    /^secure checkout$/i.test(current) ||
    /buy tickets/i.test(current) ||
    isTicketRowPriceText(current)
  );
};

export const patchPreCheckoutCtaInRoot = root => {
  if (!(root instanceof Element)) return false;
  let patched = patchTicketGroupRowCtaLabelsInRoot(root);
  root.querySelectorAll(PRECHECKOUT_CTA_SELECTOR).forEach(btn => {
    if (!(btn instanceof HTMLElement)) return;
    const current = normalizeSeaticsText(btn.textContent);
    if (current === PROMOTIX_PRE_CHECKOUT_CTA_LABEL) {
      // Still pin so future TN qty updates can't flash "Checkout".
      if (pinPreCheckoutCtaLabel(btn)) patched = true;
      return;
    }
    if (!shouldRelabelPreCheckoutCta(btn, current)) return;
    if (pinPreCheckoutCtaLabel(btn)) patched = true;
  });
  return patched;
};
